import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle2, Zap } from 'lucide-react';
import { FEATURE_CATEGORIES, ALL_FEATURES } from '../config/constants';

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── Mini SVG illustrations per category ─────────────── */
const CategoryVisual = ({ id }) => {
  const visuals = {
    rfq: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="rfqG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Inbox stack */}
        <motion.rect x="40" y="30" width="120" height="16" rx="3" fill="none" stroke="url(#rfqG)" strokeWidth="1.2"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.6 }} style={{ transformOrigin: 'left' }} />
        <motion.rect x="40" y="52" width="100" height="16" rx="3" fill="none" stroke="url(#rfqG)" strokeWidth="1.2"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.15 }} style={{ transformOrigin: 'left' }} />
        <motion.rect x="40" y="74" width="110" height="16" rx="3" fill="none" stroke="url(#rfqG)" strokeWidth="1.2"
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.3 }} style={{ transformOrigin: 'left' }} />
        {/* Check marks */}
        <motion.circle cx="28" cy="38" r="6" fill="#00D4FF" fillOpacity="0.2" stroke="#00D4FF" strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.5 }} />
        <motion.path d="M25 38 L27 40 L31 36" stroke="#00D4FF" strokeWidth="1.2" fill="none"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.6, duration: 0.3 }} />
        <motion.circle cx="28" cy="60" r="6" fill="#00D4FF" fillOpacity="0.2" stroke="#00D4FF" strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.6 }} />
        {/* Arrow routing */}
        <motion.path d="M165 38 Q180 50 165 62" stroke="#00D4FF" strokeWidth="1" fill="none" strokeDasharray="3 3"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.7, duration: 0.5 }} />
        <motion.text x="100" y="115" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Smart RFQ Routing
        </motion.text>
      </svg>
    ),
    bom: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="bomG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Tree structure */}
        <motion.circle cx="100" cy="25" r="10" fill="none" stroke="url(#bomG)" strokeWidth="1.5"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ duration: 0.4 }} />
        <motion.line x1="100" y1="35" x2="60" y2="55" stroke="url(#bomG)" strokeWidth="1"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.3 }} />
        <motion.line x1="100" y1="35" x2="140" y2="55" stroke="url(#bomG)" strokeWidth="1"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.35 }} />
        <motion.circle cx="60" cy="62" r="8" fill="none" stroke="url(#bomG)" strokeWidth="1.2"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.4 }} />
        <motion.circle cx="140" cy="62" r="8" fill="none" stroke="url(#bomG)" strokeWidth="1.2"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.45 }} />
        {/* Sub-nodes */}
        <motion.line x1="60" y1="70" x2="40" y2="88" stroke="url(#bomG)" strokeWidth="0.8"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.5 }} />
        <motion.line x1="60" y1="70" x2="80" y2="88" stroke="url(#bomG)" strokeWidth="0.8"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.55 }} />
        <motion.circle cx="40" cy="94" r="6" fill="#8B5CF6" fillOpacity="0.15" stroke="#8B5CF6" strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.6 }} />
        <motion.circle cx="80" cy="94" r="6" fill="#8B5CF6" fillOpacity="0.15" stroke="#8B5CF6" strokeWidth="1"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.65 }} />
        <motion.text x="100" y="125" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Hierarchical Structure
        </motion.text>
      </svg>
    ),
    costing: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="costG" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Bar chart */}
        {[30, 60, 90, 120, 150].map((x, i) => (
          <motion.rect key={i} x={x} y={100 - [40, 65, 50, 75, 60][i]} width="18" height={[40, 65, 50, 75, 60][i]}
            rx="3" fill="url(#costG)" fillOpacity={0.3 + i * 0.1}
            initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ delay: i * 0.1, duration: 0.5 }}
            style={{ transformOrigin: 'bottom' }} />
        ))}
        {/* Trend line */}
        <motion.path d="M39 68 L69 45 L99 55 L129 32 L159 42" fill="none" stroke="#00D4FF" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.6, duration: 0.8 }} />
        {/* Dots on trend */}
        {[[39,68],[69,45],[99,55],[129,32],[159,42]].map(([cx,cy], i) => (
          <motion.circle key={i} cx={cx} cy={cy} r="3" fill="#00D4FF"
            initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.8 + i * 0.1 }} />
        ))}
        <motion.text x="100" y="125" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Cost Analysis Engine
        </motion.text>
      </svg>
    ),
    workflow: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="wfG" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.5" />
          </linearGradient>
        </defs>
        {/* Flow nodes */}
        {[30, 80, 130].map((x, i) => (
          <motion.g key={i}>
            <motion.rect x={x} y="45" width="35" height="28" rx="6" fill="none" stroke="url(#wfG)" strokeWidth="1.2"
              initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: i * 0.2 }} />
            {i < 2 && (
              <motion.path d={`M${x + 35} 59 L${x + 45} 59`} stroke="url(#wfG)" strokeWidth="1.2"
                markerEnd="url(#arrowWf)"
                initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.3 + i * 0.2 }} />
            )}
          </motion.g>
        ))}
        {/* Diamond decision */}
        <motion.polygon points="100,85 115,97 100,109 85,97" fill="none" stroke="#F59E0B" strokeWidth="1.2"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.7 }} />
        <motion.line x1="100" y1="73" x2="100" y2="85" stroke="url(#wfG)" strokeWidth="1" strokeDasharray="3 2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.6 }} />
        <motion.text x="100" y="130" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Automated Workflows
        </motion.text>
      </svg>
    ),
    analytics: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="anG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Pie chart */}
        <motion.circle cx="70" cy="60" r="30" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 0.5 }} />
        <motion.circle cx="70" cy="60" r="30" fill="none" stroke="#00D4FF" strokeWidth="12"
          strokeDasharray="120 189" strokeDashoffset="0"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 0.65 }} transition={{ delay: 0.3, duration: 0.8 }} />
        <motion.circle cx="70" cy="60" r="30" fill="none" stroke="#8B5CF6" strokeWidth="12"
          strokeDasharray="60 189" strokeDashoffset="-120"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 0.35 }} transition={{ delay: 0.5, duration: 0.6 }} />
        {/* Mini line chart */}
        <motion.path d="M120 80 L135 60 L150 68 L165 45 L180 50" fill="none" stroke="#00D4FF" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.6, duration: 0.6 }} />
        <motion.text x="100" y="120" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Real-time Insights
        </motion.text>
      </svg>
    ),
    security: (
      <svg viewBox="0 0 200 140" className="w-full h-full">
        <defs>
          <linearGradient id="secG" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Shield shape */}
        <motion.path d="M100 20 L140 35 L140 70 Q140 95 100 110 Q60 95 60 70 L60 35 Z"
          fill="url(#secG)" fillOpacity="0.1" stroke="url(#secG)" strokeWidth="1.5"
          initial={{ pathLength: 0, fillOpacity: 0 }} whileInView={{ pathLength: 1, fillOpacity: 0.1 }}
          transition={{ duration: 1 }} />
        {/* Lock icon */}
        <motion.rect x="90" y="55" width="20" height="16" rx="3" fill="none" stroke="#14B8A6" strokeWidth="1.5"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.6 }} />
        <motion.path d="M94 55 L94 48 Q94 42 100 42 Q106 42 106 48 L106 55" fill="none" stroke="#14B8A6" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ delay: 0.7, duration: 0.4 }} />
        <motion.circle cx="100" cy="63" r="2" fill="#14B8A6"
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 0.9 }} />
        <motion.text x="100" y="130" fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle" fontFamily="Space Grotesk">
          Enterprise Security
        </motion.text>
      </svg>
    ),
  };
  return visuals[id] || null;
};

export const FeaturesPage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <div className="pt-24" data-testid="features-page">
      {/* Hero */}
      <motion.section ref={heroRef} style={{ opacity: heroOpacity }} className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00D4FF]/5 rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium"
              data-testid="features-hero-badge">
              <Zap className="w-4 h-4" /> Platform Capabilities
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Built for <span className="text-gradient-cyan">Manufacturing</span>
            </h1>
            <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
              Six integrated modules that transform your RFQ-to-SOP workflow.
              Each one designed to eliminate manual effort and drive accuracy.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Storytelling Feature Sections */}
      {FEATURE_CATEGORIES.map((cat, idx) => {
        const isReversed = idx % 2 === 1;
        return (
          <section key={cat.id} className={`py-20 md:py-28 ${idx % 2 === 1 ? 'bg-white/[0.01]' : ''}`}
            data-testid={`feature-section-${cat.id}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? '' : ''}`}>
                {/* Visual side */}
                <AnimatedSection className={isReversed ? 'lg:order-2' : ''}>
                  <div className="relative aspect-[4/3] max-w-md mx-auto">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 overflow-hidden">
                      <div className="absolute inset-0 p-6">
                        <CategoryVisual id={cat.id} />
                      </div>
                    </div>
                    {/* Floating accent */}
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-[#00D4FF]/5 border border-[#00D4FF]/10 flex items-center justify-center"
                    >
                      <cat.icon className="w-8 h-8 text-[#00D4FF]" />
                    </motion.div>
                  </div>
                </AnimatedSection>

                {/* Content side */}
                <AnimatedSection className={isReversed ? 'lg:order-1' : ''}>
                  <span className="text-[#00D4FF] text-xs font-semibold tracking-[0.2em] uppercase">
                    {String(idx + 1).padStart(2, '0')} / {cat.tagline}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-4">{cat.name}</h2>
                  <p className="text-white/50 text-sm md:text-base mb-8 leading-relaxed">{cat.description}</p>

                  <div className="space-y-3">
                    {cat.capabilities.map((cap, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: -16 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08 }}
                        className="flex items-center gap-3 group"
                      >
                        <div className="w-6 h-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00D4FF]/20 transition-colors">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#00D4FF]" />
                        </div>
                        <span className="text-white/70 text-sm">{cap}</span>
                      </motion.div>
                    ))}
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>
        );
      })}

      {/* All Features Grid */}
      <section className="py-24 border-t border-white/5" data-testid="all-features">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-xs font-semibold tracking-[0.2em] uppercase">Complete List</span>
            <h2 className="text-2xl md:text-3xl font-bold mt-4">Full Feature Breakdown</h2>
          </AnimatedSection>
          <AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {ALL_FEATURES.map((feature) => (
                <div key={feature} className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00D4FF]/20 transition-colors"
                  data-testid={`feature-item-${feature.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#00D4FF] flex-shrink-0" />
                  <span className="text-white/80 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Explore?</h2>
                <p className="text-white/50 mb-8 max-w-xl mx-auto text-sm">
                  See all these features in action with a personalized demo from our team.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/demo" className="px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all flex items-center gap-2"
                    data-testid="features-demo-btn">
                    Book a Demo <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/pricing" className="px-8 py-4 text-white font-medium rounded-full border border-white/20 hover:bg-white/5 transition-all"
                    data-testid="features-pricing-btn">
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default FeaturesPage;
